import axios from 'axios'
import Cookies from 'js-cookie'
import Router from 'next/router'
import { isDashboardAuthIntent } from '../utils/authRedirect'

const isLocalSsrLoggingEnabled = process.env.SSR_AXIOS_LOG_LOCAL === 'true'

let ssrAxiosLogger

function loadServerOnlyModule(modulePath) {
  // eslint-disable-next-line no-eval
  return eval('require')(modulePath)
}

function getSsrAxiosLoggerModulePath() {
  return `${process.cwd()}/lib/ssrAxiosLogger.js`
}

function isServerSide() {
  return typeof window === 'undefined'
}

function getSsrAxiosLogger() {
  if (!isLocalSsrLoggingEnabled || !isServerSide()) {
    return null
  }

  if (!ssrAxiosLogger) {
    ssrAxiosLogger = loadServerOnlyModule(getSsrAxiosLoggerModulePath())
  }

  return ssrAxiosLogger
}

const api = axios.create({
  baseURL: `${process.env.NEXT_PUBLIC_HOST}`,
  headers: {
    'Content-Type': 'multipart/form-data',
    Accept: 'application/json',
  },
})

api.interceptors.request.use((config) => {
  if (!isServerSide()) {
    const token = Cookies.get('SMARTCAF_USER_JWT_TOKEN')
    const lang = Cookies.get('i18next_SMARTCAF_LANG')

    if (token) {
      config.headers.Authorization = `${token}`
    } else if (config.headers.Authorization) {
      delete config.headers.Authorization
    }

    if (lang) {
      config.headers.lang = lang
    }
  }

  const logger = getSsrAxiosLogger()

  if (logger) {
    config.ssrLogStartedAt = Date.now()
    logger.logRequest(config)
  }

  return config
})

api.interceptors.response.use(
  (response) => {
    const logger = getSsrAxiosLogger()

    if (logger) {
      logger.logResponse(response)
    }

    return response
  },
  (error) => {
    const logger = getSsrAxiosLogger()

    if (logger) {
      logger.logError(error)
    }

    if (
      error.response &&
      error.response.status === 401 &&
      typeof window !== 'undefined' &&
      !error.config?.skipAuthRedirect
    ) {
      const redirectionUrl = window.location.pathname

      if (isDashboardAuthIntent(redirectionUrl)) {
        Cookies.remove('SMARTCAF_USER_JWT_TOKEN')
        try {
          localStorage.removeItem('SMARTCAF-AUTH-USER')
        } catch {
          // ignore storage errors
        }
        Router.push(`/?redirection_link=${encodeURIComponent(redirectionUrl)}`)
      }
    }
    return Promise.reject(error)
  }
)

// Custom base query that wraps the Axios instance
export const axiosBaseQuery =
  () =>
  async ({ url, method, data, params, headers, skipAuthRedirect }) => {
    try {
      const result = await api({
        url,
        method,
        data,
        params,
        headers,
        skipAuthRedirect,
      })
      return { data: result.data }
    } catch (axiosError) {
      return {
        error: {
          status: axiosError.response?.status,
          data: axiosError.response?.data,
          message: axiosError.message,
        },
      }
    }
  }

export default api
